import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import {
  ArrowRight,
  Phone,
  MapPin,
  Sparkles,
  CheckCircle2,
  Building2,
  Users,
  Star,
  Zap,
  Navigation,
  Clock,
  MessageSquare,
  Image as ImageIcon,
  FileText,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { LazyMount } from "@/components/LazyMount";
import { PageHero } from "@/components/PageHero";
import { LeadMagnetCTA } from "@/components/LeadMagnetCTA";
import { FreeAuditCTABanner } from "@/components/FreeAuditCTABanner";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import heroColorado from "@/assets/hero-colorado.jpg";
import { cityServiceHeroAlt } from "@/lib/imageAlt";
import { PageCTA } from "@/components/PageCTA";
import { LastUpdated } from "@/components/LastUpdated";
import { StickyCTABar } from "@/components/StickyCTABar";
import { CityCoverageBlock } from "@/components/CityCoverageBlock";
import { MeetChrisStrip } from "@/components/MeetChrisStrip";
import { GenericCaseStudyStrip } from "@/components/GenericCaseStudyStrip";
import type { CityPage } from "@/lib/citiesData";
import type { CityServiceConfig, CityServiceKey } from "@/lib/cityServicesData";
import { getSiblingCityCombos } from "@/lib/cityServiceMatrix";
import { getComparisonForCityService } from "@/lib/comparisonPagesData";
import { ComparisonTable } from "@/components/geo/ComparisonTable";
import { ogImageFor, ogMetaTags, twitterTextTags, type OgCityKey, type OgServiceKey } from "@/lib/ogImages";
import { geoImageObject } from "@/lib/geoImageSchema";
import { buildLocalBusinessSchema, LOCAL_BUSINESS_SCHEMA, SCHEMA_LOCALBUSINESS_ID } from "@/lib/entitySchema";
import { getPageLastUpdated } from "@/lib/lastUpdatedRegistry";

const SITE_URL = "https://coloradowebimpressions.com";
const PHONE = "+1-719-419-3935";
const PHONE_DISPLAY = "(719) 419-3935";
const HQ_ADDRESS = {
  streetAddress: "3210 E Woodmen Rd STE 110",
  addressLocality: "Colorado Springs",
  addressRegion: "CO",
  postalCode: "80920",
  addressCountry: "US",
};

// HQ geo coordinates (3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920)
const HQ_GEO = LOCAL_BUSINESS_SCHEMA.geo;

// Verified Google Business Profile CID — also used for the embedded map.
const GBP_CID_URL = "https://www.google.com/maps?cid=14271876770999369449";


// Cities that have a /{slug}-geo-ai page — used to render an SEO→GEO cross-link.
const CITIES_WITH_GEO_AI = new Set([
  "colorado-springs", "denver", "boulder", "fort-collins", "aurora", "arvada",
  "lakewood", "westminster", "thornton", "centennial", "littleton", "broomfield",
  "castle-rock", "parker", "monument", "longmont", "loveland", "pueblo",
]);

// Hours from the verified Google Business Profile.
const HQ_HOURS = LOCAL_BUSINESS_SCHEMA.openingHoursSpecification;

const whyUs = (cityName: string) => [
  { icon: Building2, label: `Serving ${cityName} from our Colorado HQ` },
  { icon: Users, label: "300+ Colorado clients served since 2006" },
  { icon: Star, label: "4.9★ average review rating from real Colorado clients" },
  { icon: CheckCircle2, label: "No long contracts. Month-to-month SEO services." },
];

type SiblingService = { key: CityServiceKey; routeBase: string; label: string };

const SIBLINGS: SiblingService[] = [
  { key: "seo", routeBase: "seo", label: "SEO" },
  { key: "web-design", routeBase: "web-design", label: "Web Design" },
];

export function buildCityServiceUrl(routeBase: string, citySlug: string) {
  // Web design uses keyword-first slug (except Colorado Springs which keeps
  // its legacy URL). All other services keep flat /{citySlug}-{routeBase}.
  if (routeBase === "web-design" && citySlug !== "colorado-springs") {
    return `${SITE_URL}/web-design-${citySlug}-colorado`;
  }
  return `${SITE_URL}/${citySlug}-${routeBase}`;
}

export function buildCityServiceHead(
  city: CityPage,
  service: CityServiceConfig,
  overrides?: {
    metaTitle?: string;
    metaDescription?: string;
    /** Pass the same overrides object the component receives so JSON-LD
     *  reflects the hand-written copy (FAQ overrides, neighborhood overrides,
     *  topical keyword overrides, market-specific descriptions). */
    pageOverrides?: CityPageOverrides;
  },
) {
  const canonical = buildCityServiceUrl(service.routeBase, city.slug);
  const title = overrides?.metaTitle ?? service.meta.title(city.name);
  const description = overrides?.metaDescription ?? service.meta.description(city.name);

  const po = overrides?.pageOverrides;
  const neighborhoods = po?.neighborhoods ?? city.neighborhoods ?? [];
  const topicalKeywords = po?.topicalKeywords ?? city.topicalKeywords ?? [];
  // Build a richer per-page LocalBusiness description: prefer the bespoke
  // hero/topical copy from overrides, then fall back to city.intro, then
  // the generic service-level description. This makes every LocalBusiness
  // payload genuinely unique across the 26 routes.
  const lbDescription =
    po?.heroDescription ??
    po?.topicalCopy?.body ??
    po?.intro ??
    city.intro ??
    description;

  // areaServed becomes an array of [City, ...named neighborhoods/subdivisions,
  // ...real ZIP codes] so Google AND AI search engines see explicit local
  // coverage we hand-write per page — not just the city name. ZIPs are a
  // strong, structured local-relevance signal that varies per page.
  const cityZips = city.zips ?? [];
  const areaServed: Array<Record<string, unknown>> = [
    {
      "@type": "City",
      name: city.name,
      containedInPlace: { "@type": "State", name: "Colorado" },
    },
    ...neighborhoods.map((n) => ({
      "@type": "Place",
      name: `${n}, ${city.name}, CO`,
    })),
    ...cityZips.map((z) => ({
      "@type": "PostalCodeArea",
      postalCode: z,
      addressRegion: "CO",
      addressCountry: "US",
    })),
  ];

  const lastUpdated = po?.lastUpdated ?? getPageLastUpdated(canonical);

  const localBusiness = {
    ...buildLocalBusinessSchema([city.name]),
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": SCHEMA_LOCALBUSINESS_ID,
    parentOrganization: { "@id": `${SITE_URL}/#org` },
    name: "Colorado Web Impressions",
    description: lbDescription,
    url: SITE_URL,
    dateModified: lastUpdated,
    telephone: PHONE,
    priceRange: "$$",
    address: { "@type": "PostalAddress", ...HQ_ADDRESS },
    // Physical HQ coordinates (verified GBP location), not the served city.
    geo: HQ_GEO,
    // Service-area coverage centroid for the city this page targets.
    areaServed,
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: city.geo.lat,
        longitude: city.geo.lng,
      },
      geoRadius: "40000",
    },
    openingHoursSpecification: HQ_HOURS,
    image: `${SITE_URL}/og-default.jpg`,
    sameAs: LOCAL_BUSINESS_SCHEMA.sameAs,
    ...(topicalKeywords.length > 0 && {
      knowsAbout: topicalKeywords,
      keywords: [
        `${service.serviceName} ${city.name}`,
        `${service.serviceName} ${city.name} CO`,
        `${city.name} ${service.serviceName.toLowerCase()}`,
        `${service.serviceName.toLowerCase()} near ${city.name}`,
        ...topicalKeywords,
        ...neighborhoods.map((n) => `${service.serviceName} ${n}`),
        ...cityZips.map((z) => `${service.serviceName} ${z}`),
      ].join(", "),
    }),
    ...(po?.whyHere && po.whyHere.length > 0 && {
      slogan: po.whyHere[0].title,
    }),
  };

  // hasOfferCatalog enumerates the named neighborhoods as discrete service
  // offerings so the Service schema is concretely different per page.
  const hasOfferCatalog = neighborhoods.length > 0
    ? {
        "@type": "OfferCatalog",
        name: `${service.serviceName} coverage in ${city.name}`,
        itemListElement: neighborhoods.map((n) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: `${service.serviceName} in ${n}, ${city.name}`,
            areaServed: { "@type": "Place", name: `${n}, ${city.name}, CO` },
          },
        })),
      }
    : undefined;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${service.serviceName} Services in ${city.name}`,
    serviceType: service.serviceType,
    description: lbDescription,
    provider: {
      "@type": "LocalBusiness",
      "@id": SCHEMA_LOCALBUSINESS_ID,
      name: "Colorado Web Impressions",
    },
    areaServed,
    url: canonical,
    dateModified: lastUpdated,
    ...(hasOfferCatalog && { hasOfferCatalog }),
  };

  // Resolve FAQ list with the same precedence the component uses:
  // serviceFaqs override > service.faqs(city), then merge city-level faqs
  // (or the override of those).
  const resolvedServiceFaqs = po?.serviceFaqs
    ?? service.faqs.map((f) => ({ q: f.q(city.name), a: f.a(city.name) }));
  const resolvedCityFaqs = po?.faqs ?? city.faqs;
  const allFaqs = [...resolvedServiceFaqs, ...resolvedCityFaqs];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${canonical}#faq`,
    mainEntity: allFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // BreadcrumbList JSON-LD mirrors the on-page <Breadcrumbs />:
  // Home → Service Areas → {City} → {Service}. This gives crawlers the same
  // hierarchy users see and earns breadcrumb rich-results in SERPs.
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${SITE_URL}/service-areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: city.name,
        item: `${SITE_URL}/service-areas/${city.slug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: service.serviceName,
        item: canonical,
      },
    ],
  };

  const ogImage = `${SITE_URL}${ogImageFor({
    service: service.key as OgServiceKey,
    city: city.slug as OgCityKey,
  })}`;

  // Twitter-specific copy: punchier than the OG/SEO description, designed
  // for the faster-scrolling Twitter/X feed where shorter, benefit-led copy wins.
  const twitterTitle = `${service.serviceName} in ${city.name}, CO — Built to Convert`;
  const twitterDescription = `Local ${service.serviceName.toLowerCase()} for ${city.name} home-service & local businesses. Real leads, no contracts, owner-led — straight from a Colorado team.`;

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      ...ogMetaTags(ogImage),
      ...twitterTextTags({ title: twitterTitle, description: twitterDescription }),
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusiness) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      // Per-page WebPage with Speakable + linked author/publisher.
      // Speakable selectors target the on-page intro paragraph (".speakable-intro"),
      // the FAQ answers (".faq-answer"), and any opt-in [data-speakable] block.
      // The author/publisher @id references stitch this page to the sitewide
      // Person + Organization graph for E-E-A-T attribution.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${canonical}#webpage`,
          url: canonical,
          name: title,
          description,
          inLanguage: "en-US",
          isPartOf: { "@id": `${SITE_URL}/#org` },
          mainEntity: { "@id": `${canonical}#localbusiness` },
          about: { "@id": `${canonical}#localbusiness` },
          keywords: [
            `${service.serviceName} ${city.name} CO`,
            `${city.name} ${service.serviceName.toLowerCase()}`,
            ...((po?.topicalKeywords ?? city.topicalKeywords ?? []).slice(0, 12)),
            ...(neighborhoods.slice(0, 6).map((n) => `${service.serviceName} ${n}`)),
          ].join(", "),
          author: { "@id": `${SITE_URL}/meet-chris#chris-heidlebaugh` },
          publisher: { "@id": `${SITE_URL}/#org` },
          primaryImageOfPage: geoImageObject({
            url: ogImage,
            caption: `${service.serviceName} in ${city.name}, CO${cityZips.slice(0, 2).length ? ` (${cityZips.slice(0, 2).join(", ")})` : ""} — Colorado Web Impressions`,
            description: `${service.serviceName} for ${city.name}, Colorado businesses${cityZips.length ? ` serving ZIPs ${cityZips.slice(0, 4).join(", ")}` : ""}. Owner-led local marketing by Colorado Web Impressions.`,
            cityName: city.name,
            zips: cityZips,
            geo: { lat: city.geo.lat, lng: city.geo.lng },
            width: 1200,
            height: 630,
          }),
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".speakable-intro", ".faq-answer", "[data-speakable]"],
          },
        }),
      },
    ],
  };
}

/** Per-page overrides — used by the static city×service route files to inject
 *  fully bespoke copy without forking the chassis. Any field present here
 *  replaces the corresponding field on the city data. */
export type CityPageOverrides = Partial<
  Pick<
    CityPage,
    | "intro"
    | "whyHere"
    | "faqs"
    | "whyThisMarket"
    | "topicalCopy"
    | "topicalKeywords"
    | "neighborhoods"
    | "nearbyCities"
  >

> & {
  /** Optional override for the service-side FAQ block (replaces service.faqs
   *  for THIS page only). Each entry is plain strings (already city-resolved). */
  serviceFaqs?: { q: string; a: string }[];
  /** Optional override for the hero title parts. */
  heroTitle?: { lead: string; gold: string; trail?: string };
  /** Optional override for the hero description paragraph. */
  heroDescription?: string;
  /** Optional override for the hero eyebrow tag. */
  heroEyebrow?: string;
  /** When set, this becomes the literal small <h1> on the page (e.g. exact
   *  keyword like "Colorado Springs SEO"); the heroTitle is demoted to <h2>. */
  h1Text?: string;
  /** ISO date for the visible "Last updated" freshness stamp under the hero. */
  lastUpdated?: string;
  /** Featured case study to render as a Springs-specific proof block. */
  caseStudy?: {
    slug: string;
    headline: string;
    heroResult: string;
    summary: string;
    quote?: { text: string; attribution: string };
  };
  /** Local testimonials filtered to this city/metro. */
  localTestimonials?: {
    author: string;
    body: string;
    rating: number;
    location?: string;
    service?: string;
    datePublished: string;
  }[];
  /** Internal links to neighborhood / sister-service pages in this market. */
  relatedLinks?: { to: string; label: string; description: string }[];
};

export function CityServicePage({
  city: rawCity,
  service,
  modifier,
  overrides,
  extraSections,
}: {
  city: CityPage;
  service: CityServiceConfig;
  /** Unique on-page sentence specific to this city × service combo. */
  modifier?: string;
  overrides?: CityPageOverrides;
  /** Optional bespoke JSX rendered just before the FAQ section. */
  extraSections?: ReactNode;
}) {

  // Merge per-page overrides on top of the city data so the static route
  // files can hand-craft any field without rewriting the chassis.
  const city: CityPage = overrides
    ? {
        ...rawCity,
        ...(overrides.intro !== undefined && { intro: overrides.intro }),
        ...(overrides.whyHere !== undefined && { whyHere: overrides.whyHere }),
        ...(overrides.faqs !== undefined && { faqs: overrides.faqs }),
        ...(overrides.whyThisMarket !== undefined && { whyThisMarket: overrides.whyThisMarket }),
        ...(overrides.topicalCopy !== undefined && { topicalCopy: overrides.topicalCopy }),
        ...(overrides.topicalKeywords !== undefined && { topicalKeywords: overrides.topicalKeywords }),
        ...(overrides.neighborhoods !== undefined && { neighborhoods: overrides.neighborhoods }),
        ...(overrides.nearbyCities !== undefined && { nearbyCities: overrides.nearbyCities }),
      }
    : rawCity;
  const heroTitle = overrides?.heroTitle ?? service.heroTitle(city.name);
  const heroDescription = overrides?.heroDescription ?? service.heroDescription(city.name);
  const heroEyebrow = overrides?.heroEyebrow ?? service.heroEyebrow(city.name);
  const serviceFaqs =
    overrides?.serviceFaqs ??
    service.faqs.map((f) => ({ q: f.q(city.name), a: f.a(city.name) }));
  const allFaqs = [...serviceFaqs, ...city.faqs];
  const otherServices = SIBLINGS.filter((s) => s.key !== service.key);
  const siblingCities = getSiblingCityCombos(service.key, city.slug);

  // Map service routeBase to a typed Link `to` so TanStack Router type-checks
  // the contextual "learn more" link below. Only seo/web-design/google-maps
  // exist as city services, matching the routeBase in cityServicesData.
  const hubLinkTo = `/${service.routeBase}` as
    | "/seo-services"
    | "/web-design"
    | "/local-seo";

  return (
    <SiteLayout>
      <PageHero
        eyebrow={heroEyebrow}
        h1Text={overrides?.h1Text}
        belowTitle={
          <LastUpdated date={overrides?.lastUpdated ?? getPageLastUpdated(buildCityServiceUrl(service.routeBase, city.slug))} />
        }
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Service Areas", to: "/service-areas" },
          { label: city.name, to: `/service-areas/${city.slug}` },
          { label: service.serviceName },
        ]}
        title={
          <>
            {heroTitle.lead}
            <span className="text-gradient-gold">{heroTitle.gold}</span>
            {heroTitle.trail}
          </>
        }
        description={heroDescription}
        image={{
          src: heroColorado,
          alt: cityServiceHeroAlt(service.serviceName, city.name, city.state),
        }}
      >
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            {service.ctaLabel(city.name)}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-5 w-5" />
            {PHONE_DISPLAY}
          </a>
        </div>
      </PageHero>

      <PricingTeaserStrip />


      {/* Above-the-fold trust + audit prompt — highest-leverage conversion
          surface on the page. Trust strip + topic-tagged /contact link so
          admin/leads shows exactly which city+service drove the submission. */}
      <section className="border-b border-border bg-gradient-to-br from-card via-card to-muted/20">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 lg:grid-cols-[1.1fr_1fr] lg:gap-10 lg:px-8 lg:py-10">
          <ul className="grid gap-3 sm:grid-cols-2">
            <li className="flex items-center gap-3 rounded-xl border border-border bg-background/60 px-4 py-3">
              <Star className="h-5 w-5 fill-current text-brand-gold-deep" />
              <span className="text-sm font-bold text-foreground">4.9★ avg from real Colorado clients</span>
            </li>
            <li className="flex items-center gap-3 rounded-xl border border-border bg-background/60 px-4 py-3">
              <Users className="h-5 w-5 text-brand-gold-deep" />
              <span className="text-sm font-bold text-foreground">300+ Colorado clients since 2006</span>
            </li>
            <li className="flex items-center gap-3 rounded-xl border border-border bg-background/60 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-brand-gold-deep" />
              <span className="text-sm font-bold text-foreground">No long contracts — month-to-month</span>
            </li>
            <li className="flex items-center gap-3 rounded-xl border border-border bg-background/60 px-4 py-3">
              <Building2 className="h-5 w-5 text-brand-gold-deep" />
              <span className="text-sm font-bold text-foreground">Colorado-based, owner-led — serving {city.name} since 2006</span>
            </li>
          </ul>

          <div className="rounded-2xl border border-brand-gold/40 bg-gradient-to-br from-brand-gold/10 via-background to-background p-5 shadow-card lg:p-6">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-brand-gold-deep">
              <Sparkles className="h-4 w-4" />
              Free {city.name} {service.serviceName} audit
            </div>
            <p className="mt-2 text-base font-bold leading-snug text-foreground">
              Get a no-pressure review of your {city.name} {service.serviceName.toLowerCase()} setup and a clear plan to win more local customers.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              We'll cover your top 3 {city.name} competitors, your Map Pack gaps, and one quick win you can ship this week. Takes ~15 min.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Link
                to="/contact"
                search={{ topic: `Free ${city.name} ${service.serviceName} audit` }}
                className="group inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-gradient-gold px-5 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                Claim my free audit
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${PHONE}`}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-bold text-foreground transition-smooth hover:border-brand-gold/50"
               aria-label="Call (719) 419-3935">
                <Phone className="h-4 w-4" />
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contextual "learn more about {Service}" link back up to the parent
          service hub. Reinforces the topic cluster: every city page links
          its service term back to /seo-services, /web-design, or /local-seo
          with keyword-rich anchor text. */}
      <section className="border-b border-border bg-section-alt">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-3 px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <p className="text-muted-foreground">
            This page covers {service.serviceName.toLowerCase()} for {city.name}{" "}
            specifically.{" "}
            <Link
              to={hubLinkTo}
              className="font-bold text-brand-gold-deep hover:underline"
            >
              Learn more about our Colorado {service.serviceName} service →
            </Link>
          </p>
          <Link
            to="/service-areas/$citySlug"
            params={{ citySlug: city.slug }}
            className="inline-flex shrink-0 items-center gap-1 font-bold text-brand-gold-deep hover:underline"
          >
            All services in {city.name}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Per-city topical SEO block — only renders when the city has unique
          topicalCopy and/or topicalKeywords filled in (citiesData.ts).
          Keeps each spoke page from being a templated copy of the others by
          surfacing real, hand-written content about that city's economy,
          trades, and search behavior. */}
      {(city.topicalCopy || (city.topicalKeywords && city.topicalKeywords.length > 0)) && (
        <section className="bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            {city.topicalCopy && (
              <>
                <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                  {service.serviceName} for the {city.name} market
                </span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                  {city.topicalCopy.heading}
                </h2>
                <p className="speakable-intro mt-4 text-lg leading-relaxed text-muted-foreground">
                  {city.topicalCopy.body}
                </p>
              </>
            )}
            {city.topicalKeywords && city.topicalKeywords.length > 0 && (
              <div className={city.topicalCopy ? "mt-8" : ""}>
                <p className="text-sm font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                  What {city.name} customers actually search
                </p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {city.topicalKeywords.map((kw) => (
                    <li
                      key={kw}
                      className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground/80 shadow-sm"
                    >
                      {kw}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ZIP coverage — real postal codes for the city's service footprint.
          Surfaced for both human credibility ("they actually serve my ZIP")
          and structured local SEO: every page renders a different list, and
          AI search engines (ChatGPT, Perplexity, Gemini, AI Overviews) lean
          on postal codes to confirm geographic relevance. Mirrors the
          PostalCodeArea entries in the LocalBusiness areaServed JSON-LD. */}
      {city.zips && city.zips.length > 0 && (
        <section className="border-y border-border bg-section-alt py-10">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              {service.serviceName} coverage in {city.name} ZIP codes
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              We deliver {service.serviceName.toLowerCase()} for {city.name},{" "}
              CO businesses across the following ZIP codes:
            </p>
            <ul
              className="mt-4 flex flex-wrap gap-2"
              aria-label={`${city.name} ZIP codes served`}
            >
              {city.zips.map((z) => (
                <li
                  key={z}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono font-semibold tracking-wider text-foreground/80"
                >
                  {z}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Why this market — long-tail topical paragraphs unique to this city.
          Same data the city hub renders, surfaced on every service spoke so
          /seo/{city}, /web-design/{city}, /google-maps/{city} each carry
          unique long-tail keyword copy instead of templated boilerplate. */}
      {city.whyThisMarket && city.whyThisMarket.length > 0 && (
        <section className="bg-section-alt py-16 lg:py-20">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <div className="text-center">
              <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                Why this market
              </span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                What makes {city.name} {service.serviceName.toLowerCase()} different
              </h2>
            </div>
            <div className="mx-auto mt-10 max-w-3xl space-y-8">
              {city.whyThisMarket.map((p) => (
                <article key={p.heading}>
                  <h3 className="text-xl font-bold text-foreground sm:text-2xl">
                    {p.heading}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured case study — opt-in proof block, only renders when overrides.caseStudy is set. */}
      {overrides?.caseStudy && (
        <section className="bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <div className="overflow-hidden rounded-3xl border border-brand-gold/30 bg-gradient-to-br from-card via-card to-muted/30 shadow-card">
              <div className="grid gap-8 p-8 lg:grid-cols-[1fr_1.4fr] lg:gap-12 lg:p-12">
                <div className="flex flex-col justify-center">
                  <span className="inline-block self-start rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                    Real {city.name} result
                  </span>
                  <p className="mt-4 text-5xl font-extrabold leading-none text-brand-gold-deep sm:text-6xl">
                    {overrides.caseStudy.heroResult}
                  </p>
                  <p className="mt-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    Documented client outcome
                  </p>
                </div>
                <div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
                    {overrides.caseStudy.headline}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    {overrides.caseStudy.summary}
                  </p>
                  {overrides.caseStudy.quote && (
                    <blockquote className="mt-5 border-l-4 border-brand-gold/50 pl-4">
                      <p className="text-sm italic leading-relaxed text-foreground/90">
                        &ldquo;{overrides.caseStudy.quote.text}&rdquo;
                      </p>
                      <footer className="mt-2 text-xs font-semibold text-muted-foreground">
                        — {overrides.caseStudy.quote.attribution}
                      </footer>
                    </blockquote>
                  )}
                  <a
                    href={`/case-studies/${overrides.caseStudy.slug}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep hover:underline"
                  >
                    Read the full case study
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Generic case-study fallback — renders only when no city-specific case study override is provided. */}
      {!overrides?.caseStudy && <GenericCaseStudyStrip />}



      {/* Local testimonials — opt-in carousel of city/metro reviews. */}
      {overrides?.localTestimonials && overrides.localTestimonials.length > 0 && (
        <section className="bg-section-alt py-16 lg:py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="text-center">
              <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                {city.name} client reviews
              </span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                What {city.name}-area owners say about working with us
              </h2>
            </div>
            <ul className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {overrides.localTestimonials.slice(0, 6).map((t) => (
                <li
                  key={`${t.author}-${t.datePublished}`}
                  className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <div className="flex items-center gap-1 text-brand-gold-deep">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/90">
                    &ldquo;{t.body}&rdquo;
                  </p>
                  <div className="mt-4 border-t border-border pt-3 text-xs">
                    <p className="font-bold text-foreground">{t.author}</p>
                    <p className="text-muted-foreground">
                      {[t.location, t.service].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep hover:underline"
              >
                Read all client reviews
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* GEO/AI cross-promo: surface the per-city GEO/AI page on every SEO city page. */}
      {service.key === "seo" && CITIES_WITH_GEO_AI.has(city.slug) && (
        <section className="bg-section-alt py-12 lg:py-14">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 lg:p-8">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
                The next layer: AI search
              </div>
              <h2 className="mt-2 text-2xl font-extrabold text-foreground sm:text-3xl">
                {city.name} buyers are starting to use ChatGPT, Gemini & Perplexity instead of Google
              </h2>
              <p className="mt-3 text-base leading-relaxed text-foreground/85">
                Traditional {city.name} SEO still matters — and we still build it for every client. But a growing share of {city.name} purchase decisions now begin inside an AI engine, not on a results page. We optimize your business for both, so you show up wherever {city.name} people are actually looking.
              </p>
              <a
                href={`/${city.slug}-geo-ai`}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-gradient-gold px-5 py-2.5 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                See {city.name} GEO/AI optimization
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Related local pages — opt-in internal-link grid for topic-cluster reinforcement. */}
      {overrides?.relatedLinks && overrides.relatedLinks.length > 0 && (
        <section className="bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="text-center">
              <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                Go deeper
              </span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                More {city.name} marketing pages
              </h2>
            </div>
            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {overrides.relatedLinks.map((l) => (
                <li key={l.to}>
                  <a
                    href={l.to}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
                  >
                    <span className="text-sm font-extrabold text-foreground group-hover:text-brand-gold-deep">
                      {l.label}
                    </span>
                    <span className="mt-1 flex-1 text-xs leading-relaxed text-muted-foreground">
                      {l.description}
                    </span>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-gold-deep">
                      Visit
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Local-vs-national on-page comparison table (renders for cities with a matching comparison config — top 5 markets). */}
      {(() => {
        const cmp = getComparisonForCityService(
          city.slug,
          service.key as "seo" | "web-design",
        );
        if (!cmp) return null;
        return (
          <section className="bg-section-alt py-16 lg:py-20">
            <div className="mx-auto max-w-4xl px-5 lg:px-8">
              <span className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
                Local vs. national — {city.name}
              </span>
              <h2 className="mt-2 text-3xl font-extrabold text-foreground sm:text-4xl">
                {city.name} {service.serviceName} agency vs. a national chain
              </h2>
              <p
                className="mt-3 max-w-2xl text-base leading-relaxed text-foreground/80"
                data-speakable
              >
                Honest, category-level comparison — owner access, contracts, pricing
                transparency, response time, and {city.name} Map Pack focus. No named
                competitors, no fake reviews of other agencies.
              </p>
              <ComparisonTable
                caption={cmp.structural.caption}
                headerA={cmp.headerA}
                headerB={cmp.headerB}
                rows={cmp.structural.rows}
              />
              <a
                href={`/${cmp.slug}`}
                className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-5 py-2.5 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                See the full {city.name} comparison
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </section>
        );
      })()}

      {/* Why this market */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Built for the {city.name} market
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {service.whyTitle(city.name)}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              {service.whyBody(city.name)}
            </p>
            {modifier && (
              <p className="mt-4 rounded-2xl border border-brand-gold/30 bg-brand-gold/5 px-5 py-4 text-base font-medium leading-relaxed text-foreground/90">
                {modifier}
              </p>
            )}
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {whyUs(city.name).map((w) => (
              <div
                key={w.label}
                className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5 shadow-card"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-gold text-brand-black">
                  <w.icon className="h-4 w-4" />
                </div>
                <p className="text-sm font-semibold text-foreground">{w.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              How we grow {city.name} businesses with {service.serviceName.toLowerCase()}
            </h2>
            <p className="mt-3 text-muted-foreground">
              Three focused pillars — built around how {city.name} customers actually search and how
              Google actually ranks local businesses today.
            </p>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {service.pillars.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                  <p.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {p.body(city.name)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      {city.neighborhoods.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                {city.name} neighborhoods we serve
              </h2>
              <p className="mt-3 text-muted-foreground">
                We build neighborhood-specific signals so you show up where your customers actually
                live and search.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {city.neighborhoods.map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground shadow-card"
                >
                  <MapPin className="h-3 w-3 text-brand-gold-deep" />
                  {n}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Get Directions / Call Now — map-driven conversion block */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-brand-gold/30 bg-gradient-to-br from-brand-black via-brand-black to-brand-black/90 shadow-gold">
            <div className="grid gap-8 p-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:p-12">
              <div>
                <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
                  Built for {city.name}, {city.state}
                </span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                  Call now or get directions
                </h2>
                <p className="mt-3 text-base leading-relaxed text-white/80">
                  Talk to a real person in Colorado — no call center, no offshore handoff. We
                  answer the phone Monday–Friday and reply to every message within one business
                  day.
                </p>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <a
                    href={`tel:${PHONE}`}
                    className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                   aria-label="Call (719) 419-3935">
                    <Phone className="h-5 w-5" />
                    Call {PHONE_DISPLAY}
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                      `${HQ_ADDRESS.streetAddress}, ${HQ_ADDRESS.addressLocality}, ${HQ_ADDRESS.addressRegion} ${HQ_ADDRESS.postalCode}`,
                    )}&destination_place_id=ChIJ_____ColoradoWebImpressions`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-2 rounded-md border border-brand-gold/50 bg-white/5 px-6 py-4 text-base font-bold text-white backdrop-blur transition-smooth hover:bg-white/10"
                  >
                    <Navigation className="h-5 w-5" />
                    Get directions
                  </a>
                </div>

                <ul className="mt-6 space-y-2 text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>
                      <strong className="text-white">Hours:</strong> Mon–Fri 9am–5pm MT · Closed
                      weekends
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>
                      <strong className="text-white">HQ:</strong> {HQ_ADDRESS.streetAddress},{" "}
                      {HQ_ADDRESS.addressLocality}, {HQ_ADDRESS.addressRegion}{" "}
                      {HQ_ADDRESS.postalCode}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold" />
                    <span>
                      <strong className="text-white">{city.name} clients:</strong> remote &
                      on-site meetings available — we travel the Front Range every week.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                <iframe
                  title={`Map of ${city.name}, ${city.state} — Colorado Web Impressions service area`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(`${city.name}, ${city.state}`)}&z=11&output=embed`}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-full min-h-[260px] w-full border-0"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Business Profile prompts — optimized for {city.name} map-pack lift */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Google Business Profile playbook
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Steal our {city.name} GBP prompts
            </h2>
            <p className="mt-3 text-muted-foreground">
              Copy, paste, and customize. These are the exact prompt formats we use to drive
              calls, direction requests, and Map Pack movement for {city.name} clients.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {[
              {
                icon: MessageSquare,
                title: `Weekly GBP post — ${city.name} update`,
                prompt: `Write a 1,500-character Google Business Profile post for a ${service.serviceName.toLowerCase()} business serving ${city.name}, ${city.state}. Open with a specific ${city.name} reference (a neighborhood like ${city.neighborhoods[0] ?? city.name}, a local landmark, or a seasonal event). Include one tangible recent project or result, two long-tail keywords (e.g. "${service.serviceName.toLowerCase()} near ${city.neighborhoods[1] ?? city.name}"), and a clear CTA — "Call (719) 419-3935" or "Get directions." No emojis in the first line. Avoid hype words.`,
              },
              {
                icon: Star,
                title: `Review request — ${city.name} customers`,
                prompt: `Draft a 2-sentence review request SMS for a ${city.name} customer who just received ${service.serviceName.toLowerCase()} work. Mention their ${city.name} neighborhood by name if known. Include a short Google review link placeholder ([REVIEW_LINK]). Tone: warm, local, never pushy. Ask them to mention what they liked most so the review reads naturally.`,
              },
              {
                icon: ImageIcon,
                title: `Photo caption — geo-tagged ${city.name} job`,
                prompt: `Write a 200-character Google Business Profile photo caption for a recently completed ${service.serviceName.toLowerCase()} project in ${city.name}, ${city.state}. Include the neighborhood (${city.neighborhoods[0] ?? city.name}), the specific service performed, and one outcome the customer cared about. Write it like a local would describe it, not like ad copy.`,
              },
              {
                icon: FileText,
                title: `Q&A seed — ${city.name} GBP listing`,
                prompt: `Generate 5 Google Business Profile Q&A pairs a ${city.name} customer would actually ask before hiring a ${service.serviceName.toLowerCase()} provider. For each: a natural-language question that includes "${city.name}" or a local neighborhood, and a 2–3 sentence answer that's honest, specific, and includes the phone number (719) 419-3935 in at least one answer. Avoid generic FAQs — anchor each one in the ${city.region} market.`,
              },
              {
                icon: Sparkles,
                title: `Service area description — ${city.name} + neighbors`,
                prompt: `Write a 600-character Google Business Profile services description for ${service.serviceName} in ${city.name}, ${city.state}. Name 3 neighborhoods (${city.neighborhoods.slice(0, 3).join(", ")}), 2 nearby cities/areas, and the typical project timeline. Include the phrase "serving ${city.name} from our Colorado Springs office" once. End with: "Call (719) 419-3935 for a free local quote."`,
              },
              {
                icon: Zap,
                title: `Offer post — drive direction requests`,
                prompt: `Write a Google Business Profile "Offer" post for a ${service.serviceName.toLowerCase()} provider serving ${city.name}. The hook is a free local consultation (no commitment). Include: a one-sentence local proof point (years serving ${city.region}, number of ${city.name} clients), a clear redemption step ("Call (719) 419-3935 or tap Directions"), and an expiration 30 days out. Keep the headline under 58 characters.`,
              },
            ].map((p) => (
              <div
                key={p.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground">{p.title}</h3>
                </div>
                <pre className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-muted/60 p-4 text-xs leading-relaxed text-foreground/90">
                  {p.prompt}
                </pre>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 text-center">
            <p className="text-sm font-semibold text-foreground">
              Want us to run your {city.name} Google Business Profile for you?
            </p>
            <div className="mt-4 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={`tel:${PHONE}`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
               aria-label="Call (719) 419-3935">
                <Phone className="h-4 w-4" />
                Call {PHONE_DISPLAY}
              </a>
              <Link
                to="/colorado-springs-google-maps"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-6 py-3 text-sm font-bold text-foreground transition-smooth hover:border-brand-gold/50"
              >
                See our local SEO service
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What's included (Web Design) */}
      {service.included && service.included.length > 0 && (
        <section className="bg-section-alt py-20 lg:py-28">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                What every {city.name} {service.serviceName.toLowerCase()} project includes
              </h2>
              <p className="mt-3 text-muted-foreground">
                Standard on every project — no surprise add-ons, no nickel-and-diming.
              </p>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {service.included.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                  <span className="text-sm font-semibold text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Lead magnet */}
      <LeadMagnetCTA variant="feature" />


      {extraSections}

      <FreeAuditCTABanner
        headline={`Get your free ${city.name} ${service.serviceName.toLowerCase()} audit`}
      />



      {/* FAQ */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {city.name} {service.serviceName} FAQs
            </h2>
            <p className="mt-3 text-muted-foreground">
              Honest answers to the questions {city.name} business owners ask us most.
            </p>
          </div>
          <div className="mt-10 space-y-4">
            {allFaqs.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <h3 className="text-base font-bold text-foreground">{f.q}</h3>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>

          {/* Cross-links to other services in this city + city hub */}
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {otherServices.map((s) => (
              <a
                key={s.key}
                href={buildCityServiceUrl(s.routeBase, city.slug).replace(SITE_URL, "")}
                className="group rounded-2xl border border-border bg-card p-5 text-sm font-bold text-foreground shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                {s.key === "web-design" ? (
                  <Sparkles className="h-5 w-5 text-brand-gold-deep" />
                ) : (
                  <Zap className="h-5 w-5 text-brand-gold-deep" />
                )}
                <div className="mt-3">
                  {city.name} {s.label}
                </div>
                <div className="mt-1 text-xs font-medium text-muted-foreground">
                  Pair with {s.label.toLowerCase()} that ranks and converts.
                </div>
              </a>
            ))}
            <Link
              to="/service-areas/$citySlug"
              params={{ citySlug: city.slug }}
              className="group rounded-2xl border border-border bg-card p-5 text-sm font-bold text-foreground shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
            >
              <MapPin className="h-5 w-5 text-brand-gold-deep" />
              <div className="mt-3">{city.name} hub</div>
              <div className="mt-1 text-xs font-medium text-muted-foreground">
                All services we offer in {city.name}.
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Sibling-city silo: same service in other Colorado cities */}
      {siblingCities.length > 0 && (
        <LazyMount minHeight={400} rootMargin="600px 0px">
          <section className="bg-background py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                  {service.serviceName} across Colorado
                </span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                  {service.serviceName} in other Colorado cities
                </h2>
                <p className="mt-3 text-muted-foreground">
                  Serve customers in more than one Colorado market? We run{" "}
                  {service.serviceName.toLowerCase()} programs in every city below.
                </p>
              </div>
              <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {siblingCities.map((c) => (
                  <li key={c.city.slug}>
                    <a
                      href={c.path}
                      className="group flex h-full items-start gap-3 rounded-2xl border border-border bg-card p-5 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
                    >
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                        <MapPin className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-extrabold text-foreground group-hover:text-brand-gold-deep">
                          {service.serviceName} in {c.city.name}
                        </span>
                        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                          {c.city.region} · serving {c.city.neighborhoods.slice(0, 3).join(", ")}
                        </span>
                      </span>
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </LazyMount>
      )}

      <CityCoverageBlock
        cityName={city.name}
        cityState={city.state}
        neighborhoods={city.neighborhoods}
        zips={city.zips}
        nearbyCities={city.nearbyCities}
      />

      <MeetChrisStrip />

      <PageCTA
        headline={`Ready to grow your ${city.name} business with ${service.serviceName.toLowerCase()}?`}
        description={`We'll review your current ${service.serviceName.toLowerCase()} setup, your top ${city.name} competitors, and give you a clear, no-pressure plan to win more local customers.`}
        seeOurWork={{
          to: "/reviews",
          label: `See real results for Colorado businesses like yours`,
        }}
      />
      <StickyCTABar />
    </SiteLayout>
  );
}
